import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowRight, Sparkles, Shield, ArrowLeft } from "lucide-react";
import { trackAdsConversion } from "@/lib/ads";

/**
 * Hero signup with a two-step flow:
 *   Step 1: Name + Email + [Get my account]
 *   Step 2: Password + Confirm Password + [Create my account]
 *           or [Continue with Google] as an escape hatch.
 */
export default function HeroSignup() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const nextStep = (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    setStep(2);
  };

  const createAccount = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/auth/register", {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        password,
        role: "candidate",
      });
      localStorage.setItem("vw_token", r.data.token);
      localStorage.setItem("vw_returning", "1");
      setUser(r.data.user);
      trackAdsConversion("signup", { method: "email" });
      toast.success(`Welcome to KARM, ${r.data.user.name.split(" ")[0]}.`);
      navigate("/dashboard");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't sign you up";
      if (/already/i.test(msg)) {
        toast.error("That email already has an account — sign in instead.");
        navigate("/auth");
      } else {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="max-w-2xl mx-auto" data-testid="hero-signup-block">
      {step === 1 && (
        <>
          <form
            onSubmit={nextStep}
            className="vital-card-static search-shadow rounded-2xl p-2 grid grid-cols-1 md:grid-cols-[1fr_1.4fr_auto] gap-2 items-stretch"
            data-testid="hero-signup-form"
          >
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              required
              className="border-0 focus-visible:ring-0 shadow-none h-12 px-4"
              data-testid="hero-signup-name"
            />
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              required
              className="border-0 focus-visible:ring-0 shadow-none h-12 px-4 md:border-l md:border-border"
              data-testid="hero-signup-email"
            />
            <Button
              type="submit"
              size="lg"
              className="h-12 px-6 rounded-xl"
              data-testid="hero-signup-submit"
            >
              Get my account <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </form>

          <div className="mt-3 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm">
            <button
              type="button"
              onClick={handleGoogle}
              className="font-semibold inline-flex items-center gap-1.5 text-foreground hover:text-primary transition-colors"
              data-testid="hero-google-btn"
            >
              <Sparkles className="h-3.5 w-3.5" /> Continue with Google
            </button>
            <span className="text-muted-foreground hidden md:inline">·</span>
            <button
              type="button"
              onClick={() => navigate("/auth")}
              className="text-muted-foreground hover:text-foreground transition-colors"
              data-testid="hero-signin-link"
            >
              Already have an account? <span className="underline">Sign in</span>
            </button>
          </div>
        </>
      )}

      {step === 2 && (
        <>
          <form
            onSubmit={createAccount}
            className="vital-card-static search-shadow rounded-2xl p-5 md:p-6 space-y-3 text-left"
            data-testid="hero-password-form"
          >
            <div className="flex items-center justify-between mb-2">
              <div>
                <h3 className="text-base font-bold leading-tight">Pick a password</h3>
                <p className="text-xs text-muted-foreground mt-0.5 truncate">
                  for <span className="font-medium text-foreground">{email}</span>
                </p>
              </div>
              <button
                type="button"
                onClick={() => setStep(1)}
                className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                data-testid="hero-back-btn"
              >
                <ArrowLeft className="h-3.5 w-3.5" /> Back
              </button>
            </div>

            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password (at least 8 characters)"
              required
              minLength={8}
              autoComplete="new-password"
              className="h-11"
              data-testid="hero-password-input"
            />
            <Input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Confirm password"
              required
              minLength={8}
              autoComplete="new-password"
              className="h-11"
              data-testid="hero-password-confirm"
            />
            <Button
              type="submit"
              size="lg"
              disabled={loading}
              className="w-full h-11 rounded-xl"
              data-testid="hero-create-account-btn"
            >
              {loading ? "Creating your account…" : (<>Create my account <ArrowRight className="ml-1 h-4 w-4" /></>)}
            </Button>
          </form>

          <div className="mt-3 flex items-center gap-3 max-w-2xl mx-auto">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs uppercase tracking-wider text-muted-foreground">or, skip the password</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <Button
            type="button"
            variant="outline"
            size="lg"
            onClick={handleGoogle}
            className="w-full mt-3 h-11 rounded-xl"
            data-testid="hero-google-btn-step2"
          >
            <Sparkles className="h-4 w-4 mr-2" /> Continue with Google
          </Button>
        </>
      )}

      <p className="mt-3 text-xs text-muted-foreground text-center inline-flex items-center justify-center gap-1.5 w-full">
        <Shield className="h-3 w-3" /> Free to join · No card needed · Your data stays yours.
      </p>
    </div>
  );
}
